import { getErrorObject } from '@quickmd-llc/frontend-utils'

import { apiErrorCodes } from '@/utils/apiHelper'

const FALLBACK = 'Could not save productivity settings. Please try again.'

/**
 * The complete set of `ErrorCode`s `PUT /providers/{userId}/productivityoverrides` can return —
 * see `ChangeDoctorProductivityOverridesAdminApiRequestHandler` on the backend, which sets
 * `ProviderNotFound`, `ProductivityOverrideNotValid` (duration outside the allowed range) and
 * `VisitTypeNotFound`. Anything outside this map is genuinely unknown and gets the generic copy.
 */
const MESSAGE_BY_ERROR_CODE: Record<string, string> = {
  [apiErrorCodes.PROVIDER_NOT_FOUND]: 'This provider no longer exists. Refresh the page and try again.',
  [apiErrorCodes.PRODUCTIVITY_OVERRIDE_NOT_VALID]:
    'That appointment length is outside the allowed range for this visit type.',
  [apiErrorCodes.VISIT_TYPE_NOT_FOUND]:
    'This visit type is no longer available on the provider’s license. Refresh the page and try again.',
}

/** Maps a failed productivity-override write into user-facing copy. */
export function getProductivityErrorMessage(error: unknown): string {
  const code = readErrorCode(error)
  const mapped = code === undefined ? undefined : MESSAGE_BY_ERROR_CODE[code]
  if (mapped !== undefined) return mapped

  // Only a real Error carries prose worth showing (e.g. a network failure). `getErrorObject`
  // serializes an unrecognised payload into JSON, which is not something to put in front of a VA,
  // so anything else gets the generic message.
  return error instanceof Error && error.message ? getErrorObject(error, FALLBACK).message : FALLBACK
}

/** The error body is `ErrorDescription` / `ErrorApiOutContract` — both carry a string `ErrorCode`. */
function readErrorCode(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') return undefined

  const body =
    'body' in error ? error.body
    : 'response' in error ? error.response
    : error
  if (!body || typeof body !== 'object') return undefined

  const code = 'ErrorCode' in body ? body.ErrorCode : undefined
  return typeof code === 'string' ? code : undefined
}
